import { RolesBadgeList } from './RolesBadgeList';
import { formatAbsoluteDateTime, formatRelativeDate } from '../../lib/formatters';
import type { UserV2Output } from '../../types/extranet-api.types';
import { IconInbox } from '../incidents/icons';

interface UsersTableProps {
  items: UserV2Output[];
  total: number;
  isLoading: boolean;
  isFetching: boolean;
  canManage: boolean;
  onEditRoles: (user: UserV2Output) => void;
}

const SKELETON_ROWS = 8;
const COL_COUNT = 8; // Nom · Email · Rôles · Type · Compte · Réservations · Créé · MAJ · (Actions)

export const UsersTable = ({ items, total, isLoading, isFetching, canManage, onEditRoles }: UsersTableProps) => {
  const colSpan = canManage ? COL_COUNT + 1 : COL_COUNT;
  return (
    <div className="bg-white border border-zinc-200 rounded-md overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b border-zinc-200 bg-zinc-50/40">
              <Th>Nom</Th>
              <Th>Email</Th>
              <Th>Rôles</Th>
              <Th>Type</Th>
              <Th>Compte</Th>
              <Th align="right">Réservations</Th>
              <Th>Créé</Th>
              <Th>Statut</Th>
              {canManage ? <Th align="right">Actions</Th> : null}
            </tr>
          </thead>
          <tbody className={isFetching && !isLoading ? 'opacity-60 transition-opacity' : ''}>
            {isLoading ? (
              Array.from({ length: SKELETON_ROWS }).map((_, i) => <SkeletonRow key={i} hasActions={canManage} />)
            ) : items.length === 0 ? (
              <EmptyRow colSpan={colSpan} />
            ) : (
              items.map(user => (
                <tr
                  key={user.id}
                  className="border-b border-zinc-100 last:border-b-0 hover:bg-zinc-50/60 transition-colors duration-100"
                >
                  <td className="px-4 py-3 text-zinc-900 whitespace-nowrap font-medium">
                    {user.firstName} {user.lastName}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs tabular-nums text-zinc-700 whitespace-nowrap">
                    {user.email}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <RolesBadgeList roles={user.roles} />
                  </td>
                  <td className="px-4 py-3 text-xs text-zinc-700 whitespace-nowrap">{user.type}</td>
                  <td className="px-4 py-3 text-xs text-zinc-700 whitespace-nowrap">{user.kind}</td>
                  <td className="px-4 py-3 font-mono text-xs tabular-nums text-zinc-700 text-right whitespace-nowrap">
                    {user.bookingCounter}
                  </td>
                  <td
                    className="px-4 py-3 font-mono text-xs tabular-nums text-zinc-500 whitespace-nowrap"
                    title={formatAbsoluteDateTime(user.createdAt)}
                  >
                    {formatRelativeDate(user.createdAt)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <ActivedBadge actived={user.actived} />
                  </td>
                  {canManage ? (
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button
                        type="button"
                        onClick={() => onEditRoles(user)}
                        className="text-xs text-zinc-700 hover:text-zinc-900 hover:underline underline-offset-2"
                      >
                        Modifier les rôles
                      </button>
                    </td>
                  ) : null}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {!isLoading && items.length > 0 ? (
        <div className="px-4 py-2.5 text-xs text-zinc-500 border-t border-zinc-100">
          <span className="font-mono tabular-nums text-zinc-700">{items.length}</span> sur{' '}
          <span className="font-mono tabular-nums text-zinc-700">{total}</span> utilisateurs
        </div>
      ) : null}
    </div>
  );
};

const ActivedBadge = ({ actived }: { actived?: boolean }) => {
  if (actived === undefined) {
    return <span className="text-xs text-zinc-400">—</span>;
  }
  const isActive = actived === true;
  return (
    <span
      className={[
        'inline-flex items-center gap-1.5 rounded-md text-xs font-medium px-2 py-0.5',
        isActive ? 'bg-emerald-50 text-emerald-700' : 'bg-zinc-100 text-zinc-500',
      ].join(' ')}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${isActive ? 'bg-emerald-500' : 'bg-zinc-400'}`} aria-hidden />
      {isActive ? 'Actif' : 'Inactif'}
    </span>
  );
};

const Th = ({ children, align = 'left' }: { children: React.ReactNode; align?: 'left' | 'right' }) => (
  <th
    className={`px-4 py-2.5 text-xs font-medium uppercase tracking-wide text-zinc-500 ${align === 'right' ? 'text-right' : 'text-left'}`}
  >
    {children}
  </th>
);

const SkeletonRow = ({ hasActions }: { hasActions: boolean }) => (
  <tr className="border-b border-zinc-100 last:border-b-0">
    {Array.from({ length: hasActions ? COL_COUNT + 1 : COL_COUNT }).map((_, i) => (
      <td key={i} className="px-4 py-3">
        <div
          className="h-3 rounded-sm skeleton-pulse"
          style={{ width: `${[60, 75, 50, 40, 45, 30, 50, 40, 35][i] ?? 50}%` }}
        />
      </td>
    ))}
  </tr>
);

const EmptyRow = ({ colSpan }: { colSpan: number }) => (
  <tr>
    <td colSpan={colSpan} className="px-4 py-16">
      <div className="flex flex-col items-center justify-center gap-3 text-center">
        <IconInbox width={28} height={28} className="text-zinc-300" />
        <div>
          <p className="text-sm text-zinc-900">Aucun utilisateur sur ce compte.</p>
          <p className="text-xs text-zinc-500 mt-1">Ajustez les filtres pour afficher davantage de résultats.</p>
        </div>
      </div>
    </td>
  </tr>
);
